import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SEO from "./SEO";
import Header from "./Header";
import Footer from "./Footer";
import JourneyTracker from "./JourneyTracker";
import { ShieldCheck, Clock, Package } from "lucide-react";
import {IMAGES as CarriersLogo} from "../Assets/Images/index";
// ── Ridelink Official Brand Palette ──
// Primary: Navy #022C43 | Green #1B998B | Red-Orange #E84856 | Sun Yellow #FFDE14
// Proportions: Navy dominant → Green secondary → Red/Yellow sparingly
const NAVY = "#022C43";   // Navy Blue — dominant primary
const GREEN = "#1B998B";  // Green — second primary
const DNAV = "#011825";   // Dark navy — depth/footer
const LNAV = "#0A3D5C";   // Light navy — hover
const RED  = "#E84856";   // Red Orange — CTA/alert (sparingly)
const YELLOW = "#FFDE14"; // Sun Yellow — highlight (very sparingly)
// LEGACY aliases kept for backward compat
const TEAL = GREEN;
const TNAV = DNAV;

const CARRIERS = [
  { name: "Maersk",       logo: CarriersLogo.maersk },
  { name: "MSC",          logo: CarriersLogo.msc },
  { name: "CMA CGM",      logo: CarriersLogo.cmaCgm },
  { name: "Hapag-Lloyd",  logo: CarriersLogo.hapagLloyd },
  { name: "ONE",          logo: CarriersLogo.one },
  { name: "HMM",          logo: CarriersLogo.hmm },
  { name: "Yang Ming",    logo: CarriersLogo.yangMing },
  { name: "Wan Hai",      logo: CarriersLogo.wanHai },
  { name: "ZIM",          logo: CarriersLogo.zim },
  { name: "Matson",       logo: CarriersLogo.matson },
  { name: "PIL",          logo: CarriersLogo.pil },
  { name: "Triton",       logo: CarriersLogo.triton },
  { name: "EMC",          logo: CarriersLogo.emc },
]; 

const FEATURES = [
  {
    icon: "⚡",
    title: "Lightning Fast",
    desc: "Results in seconds with optimized search",
  },
  {
    icon: "🌍",
    title: "Global Coverage",
    desc: "100+ carriers across all major trade routes",
  },
  {
    icon: "🔒",
    title: "Secure & Private",
    desc: "Encrypted data, never shared with third parties",
  },
  {
    icon: "🔔",
    title: "Smart Alerts",
    desc: "Instant notifications for status changes",
  },
];

const SERVICES = [
  {
    label: "Sea Shipping",
    emoji: "🚢",
    bg: `linear-gradient(135deg,${NAVY} 0%,${LNAV} 100%)`,
  },
  {
    label: "Air Shipping",
    emoji: "✈️",
    bg: `linear-gradient(135deg,${LNAV} 0%,${DNAV} 100%)`,
  }
];

const STEPS = [
  {
    n: "01",
    title: "Enter Tracking Number",
    desc: "Paste or type your tracking number in the search field",
  },
  {
    n: "02",
    title: "Get Instant Results",
    desc: "We search across multiple carriers to find your package",
  },
  {
    n: "03",
    title: "Track in Real-time",
    desc: "View detailed info and estimated delivery time",
  },
];

const STATS = [
  { num: "145+", label: "Countries", emoji: "🌍" },
  { num: "3K+",  label: "Happy Customers", emoji: "❤️" },
  { num: "5M+",  label: "Shipments Tracked", emoji: "🔍" },
];

const SEA_FEATURES = [
  "Real-time container status & port events",
  "Live Map with vessel position tracking",
  "ETD / ETA predictions per voyage",
  "All-in-one multi-carrier dashboard",
];

const AIR_FEATURES = [
  "Flight info & air waybill (AWB) tracking",
  "Split shipments visibility across flights",
  "All-in-one dashboard for every airline",
  "Instant exception & delay alerts",
];

const SEA_LOGOS = ["Maersk", "MSC", "CMA CGM", "Hapag-Lloyd", "ONE", "HMM", "Yang Ming"];
const AIR_LOGOS  = ["Maersk", "CMA CGM", "ONE", "ZIM", "PIL"];
const slides = [
  {
    title: "Track Every Shipment\nIn Real Time",
    subtitle: "Live Updates | Smart Alerts | Total Visibility",
    image: "/slides/Gemini_Generated_Image_vl9r8kvl9r8kvl9r-removebg-preview.png"
  },
  {
    title: "Smarter Logistics\nStart Here",
    subtitle: "AI Insights | Predictive Tracking | Faster Decisions",
    image: "/slides/Gemini_Generated_Image_52n6qf52n6qf52n6-removebg-preview.png"
  },
  {
    title: "Global Shipment\nTracking Made Easy",
    subtitle: "Ocean | Air | Land | One Platform",
    image: "/slides/Gemini_Generated_Image_g81kmqg81kmqg81k-removebg-preview.png"
  },
  {
    title: "Your Cargo\nAlways in Sight",
    subtitle: "Real-Time Tracking | Secure Data | 24/7 Monitoring",
    image: "/slides/fd5d360ff078dc897e88dbed7c95118e-removebg-preview.png"
  }
];

// Background phases: 0 = map dominant, 1 = pin dominant
const BG_PHASES = [0, 1, 0, 1];

export default function LandingPage() {
  const [currentSlide, setCurrentSlide] = useState(0);

  // ── STATS COUNTER ──
  const statsRef = useRef(null);
  const [counts, setCounts] = useState(STATS.map(() => 0));
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated) {
        setHasAnimated(true);
        STATS.forEach((stat, i) => {
          const target = parseInt(stat.num);
          const duration = 2000;
          const startTime = performance.now();
          const animate = (now) => {
            const progress = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCounts(prev => {
              const next = [...prev];
              next[i] = Math.floor(eased * target);
              return next;
            });
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        });
      }
    }, { threshold: 0.3 });
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  // phase: 'fg-enter' | 'fg-hold' | 'fg-exit' | 'bg-shift' | 'fg2-enter'
  const [animPhase, setAnimPhase] = useState('fg-enter');
  const [bgPhase, setBgPhase]   = useState(0); // 0 or 1
  const [fgVisible, setFgVisible] = useState(true);

  useEffect(() => {
    // Full cycle per slide:
    // 0-800ms   fg-enter  (foreground image animates in)
    // 800-3000ms fg-hold  (foreground stays)
    // 3000-3800ms fg-exit (foreground animates out)
    // 3800-4600ms bg-shift (background cross-fades to next state)
    // 4600-5000ms fg2-enter (new foreground enters, slide index advances)
    let t1, t2, t3, t4, t5;
    const runCycle = () => {
      setAnimPhase('fg-enter');
      setFgVisible(true);
      t1 = setTimeout(() => setAnimPhase('fg-hold'), 800);
      t2 = setTimeout(() => { setAnimPhase('fg-exit'); setFgVisible(false); }, 3000);
      t3 = setTimeout(() => {
        setAnimPhase('bg-shift');
        setBgPhase(p => (p + 1) % 2);
      }, 3800);
      t4 = setTimeout(() => {
        setCurrentSlide(prev => (prev + 1) % slides.length);
        setAnimPhase('fg-enter');
        setFgVisible(true);
      }, 4600);
    };
    runCycle();
    const loop = setInterval(runCycle, 5200);
    return () => { clearInterval(loop); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
  }, []);

  const [tracking, setTracking] = useState("");
  const [dest, setDest] = useState("");

  const [trackingNumber, setTrackingNumber] = useState("");
  const navigate = useNavigate();
  const { user, creditBalance, isAuthInitialized } = useAuth();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;
    const params = new URLSearchParams();
    params.set("tracking", trackingNumber.trim());
    navigate(`/results?${params.toString()}`);
  };
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "RideLink Tracking",
    "url": "https://tracking.ride-link.com/",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://tracking.ride-link.com/results?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <div style={s.root}>
      <SEO
        title="RideLink Container Tracking"
        description="Track containers and shipments across Maersk, MSC, CMA CGM, Hapag-Lloyd, Evergreen, COSCO and more with RideLink Tracking."
        canonicalPath="/"
        structuredData={structuredData}
      />
      <style>{css}</style>

      {/* ── NAV ── */}
      <Header />

      {/* ── TRACKER ── */}
      <JourneyTracker />

      {/* ── HERO ── */}
      <section style={s.hero} id="hero" className="lp-hero">
        {/* ── LAYERED MAP BACKGROUNDS ── */}
        {/* Layer 1: World map dots – always present, opacity shifts with bgPhase */}
        <div
          style={{
            ...s.heroBgMap,
            opacity: bgPhase === 0 ? 0.18 : 0.08,
            transform: bgPhase === 0 ? 'scale(1) translateX(0%)' : 'scale(1.06) translateX(-2%)',
          }}
        />
        {/* Layer 2: Map pin cluster – always present, opacity shifts with bgPhase */}
        <div
          style={{
            ...s.heroBgPin,
            opacity: bgPhase === 1 ? 0.22 : 0.07,
            transform: bgPhase === 1 ? 'scale(1) translateX(0%)' : 'scale(0.96) translateX(2%)',
          }}
        />
        {/* Tint overlay so content stays readable */}
        <div style={s.heroBgTint} />

        {isAuthInitialized && user ? (
          /* ── Logged-in dashboard hero ── */
          <div style={s.heroContent} className="lp-hero-content">
            <div style={s.userWelcome}>
              {user.picture ? (
                <img src={user.picture} alt={user.name} style={s.userAvatar} referrerPolicy="no-referrer" />
              ) : (
                <div style={s.userAvatarInitials}>{user.name?.charAt(0).toUpperCase() || 'U'}</div>
              )}
              <div>
                <p style={s.userGreeting}>Welcome back</p>
                <h2 style={s.userName}>{user.name}</h2>
              </div>
            </div>

            <div style={s.userCredits}>
              <span style={s.userCreditsNum}>{creditBalance}</span>
              <span style={s.userCreditsSub}>credits remaining</span>
            </div>

            <button
              style={s.trackNowBtn}
              className="track-now-btn"
              onClick={() => navigate('/results')}
            >
              Track a Shipment →
            </button>

            <button
              style={s.viewProfileLink}
              className="nav-a"
              onClick={() => navigate('/profile')}
            >
              View profile &amp; history
            </button>
          </div>
        ) : (
          /* ── Guest hero ── */
          <div style={s.heroContent} className="lp-hero-content">
            
            {/* Search Box on Top */}
            <div style={s.searchBarContainer}>
              <div style={s.searchBar} className="search-bar">
                {/* <span style={s.searchIcon}>📍</span> */}
                <input
                  style={s.searchInput}
                  placeholder="Enter Tracking Number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
                {/* <div style={s.searchDivider} /> */}
                {/* <span style={s.searchIcon}>📦</span> */}
                <button
                  style={s.searchBtn}
                  className="search-btn"
                  onClick={handleSubmit}
                >
                  🔍
                </button>
              </div>
            </div>

          

            {/* Sliding Title */}
            <h1
              style={s.heroH1}
              className={`hero-text ${
                animPhase === 'fg-enter' || animPhase === 'fg-hold' ? 'hero-text--in' :
                animPhase === 'fg-exit' ? 'hero-text--out' : 'hero-text--hidden'
              }`}
            >
              {slides[currentSlide].title.split('\n').map((line, i) => (
                <span key={i}>{line}<br/></span>
              ))}
            </h1>

            {/* Sliding Subtitle Icons */}
            <div
              style={s.heroSubIcons}
              className={`hero-text ${
                animPhase === 'fg-enter' || animPhase === 'fg-hold' ? 'hero-text--in' :
                animPhase === 'fg-exit' ? 'hero-text--out' : 'hero-text--hidden'
              }`}
            >
              {slides[currentSlide].subtitle.split('|').map((item, idx) => (
                <div key={idx} style={s.heroIconBox} className="lp-hero-icon-box">
                  <div style={s.heroIconTeal}>
                    {idx === 0 ? <Package size={40} /> : idx === 1 ? <Clock size={40} /> : <ShieldCheck size={40} />}
                  </div>
                  <div style={s.heroIconText}>{item.trim()}</div>
                </div>
              ))}
            </div>

          </div>
        )}

        {/* Hero Sliding Foreground Image */}
        <div style={s.containerWrap} className="lp-hero-img-wrap">
          <img
            key={currentSlide}
            src={slides[currentSlide].image}
            alt={slides[currentSlide].title.replace('\n', ' ')}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            className={`hero-fg-img ${
              animPhase === 'fg-enter' ? 'hero-fg-img--enter' :
              animPhase === 'fg-hold'  ? 'hero-fg-img--hold'  :
              animPhase === 'fg-exit'  ? 'hero-fg-img--exit'  : 'hero-fg-img--hidden'
            }`}
          />
        </div>

        {/* Slide Indicators */}
        <div style={s.slideIndicators}>
          {slides.map((_, i) => (
            <div
              key={i}
              style={{
                ...s.slideDot,
                background: currentSlide === i ? NAVY : `${NAVY}40`,
                width: currentSlide === i ? 24 : 8
              }}
            />
          ))}
        </div>

        {/* Animated bg-phase label (subtle) */}
        <div style={s.bgPhaseTag} className="bg-phase-tag">
          {bgPhase === 0 ? '🗺 Global Coverage' : '📍 Precise Tracking'}
        </div>
      </section>

      {/* ── CARRIERS MARQUEE ── */}
      <section style={s.carriers}>
        <p style={s.carriersLabel}>Trusted by the world's leading carriers</p>
        <div style={s.carriersTrack}>
          <div style={s.carriersSlide} className="carriers-slide">
            {[...CARRIERS, ...CARRIERS].map((c, i) => (
              <div key={i} style={s.carrierPill}>
                 <img src={c.logo} alt={c.name} style={s.carrierImg} type="image/png" />
                <span style={s.carrierName}>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsRef} style={s.statsSection} className="lp-stats-section">
        <div style={s.statsInner}>
          <p style={s.statsHeading}>Providing visibility across borders</p>
          <div style={s.statsGrid} className="lp-stats-grid">
            {STATS.map((stat, i) => (
              <div key={stat.label} style={s.statItem}>
                <span style={s.statNum}>{counts[i] + stat.num.replace(/\d+/, '')}</span>
                <span style={s.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={s.how} id="how-it-works" className="lp-section">
        <div style={s.sectionInner} className="lp-section-inner">
          <p style={s.eyebrow}>Simple Process</p>
          <h2 style={s.sectionTitle} className="lp-section-title">How It Works</h2>
          <p style={s.sectionSub}>Track your package in three simple steps</p>
          <div style={s.steps} className="lp-grid-3">
            {STEPS.map((step) => (
              <div key={step.n} style={s.stepCard} className="step-card">
                <div style={s.stepNum}>{step.n}</div>
                <h3 style={s.stepTitle}>{step.title}</h3>
                <p style={s.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── SERVICES ── */}
      {/* <section style={s.services} id="services" className="lp-section">
        <div style={s.sectionInner} className="lp-section-inner">
          <p style={s.eyebrow}>What We Offer</p>
          <h2 style={s.sectionTitle} className="lp-section-title">Shipping & Logistics Services</h2>
          <div style={s.serviceCards} className="lp-grid-3">
            {SERVICES.map((sv) => (
              <div
                key={sv.label}
                style={{ ...s.serviceCard, background: sv.bg }}
                className="service-card"
                onClick={() => navigate(`/tracking`)}
              >
                <div style={s.serviceEmoji}>{sv.emoji}</div>
                <h3 style={s.serviceLabel}>{sv.label}</h3>
                <div style={s.serviceArrow}>→</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ── WHY CHOOSE ── */}
      <section style={s.why} id="features" className="lp-section">
        <div style={s.whyInner} className="lp-grid-2 lp-reverse-mobile">
          <div style={s.whyLeft}>
             <p style={s.eyebrow}>Why Choose Us</p>
            <h2 style={s.whyH2}>
              Powering Logistics
              <br />
              Across Business
            </h2>
            <p style={s.whySub}>
              The most comprehensive shipment tracking platform with advanced
              features designed for modern logistics operations.
            </p>
            <ul style={s.featureList}>
              {FEATURES.map((f) => (
                <li
                  key={f.title}
                  style={s.featureItem}
                  className="feature-item"
                >
                  <div style={s.featureIcon}>{f.icon}</div>
                  <div>
                    <h4 style={s.featureTitle}>{f.title}</h4>
                    <p style={s.featureDesc}>{f.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div style={s.whyRight}>
            <img
              src="/Gemini_Generated_Image_e7zupze7zupze7zu-removebg-preview.png"
              alt="Ridelink shipment tracking"
              style={{ width: "100%", maxWidth: 480, display: "block", margin: "0 auto" }}
            />
          </div>
        </div>
      </section>

      {/* ── SEA CONTAINER TRACKING ── */}
      <section style={s.productSection} id="sea-tracking" className="lp-product-section">
        <div style={s.productInner} className="lp-product-inner">
          <div style={s.productLeft}>
            <p style={s.eyebrow}>Sea Freight</p>
            <h2 style={s.productHeading}>Real-Time Container<br />Tracking</h2>
            <p style={s.productPara}>
              Ridelink provides everything you need for container tracking in one place.
              Instantly track containers across all major shipping lines, save time and
              money by simplifying your supply chain visibility.
            </p>
            <ul style={s.productCheckList}>
              {SEA_FEATURES.map((f) => (
                <li key={f} style={s.productCheckItem}>
                  <span style={s.productCheckIcon}>✓</span>
                  {f}
                </li>
              ))}
            </ul>
            <div style={s.productLogoRow}>
              {CARRIERS.filter((c) => SEA_LOGOS.includes(c.name)).map((c) => (
                <img key={c.name} src={c.logo} alt={c.name} style={s.productLogoImg} title={c.name} />
              ))}
            </div>
          </div>
          <div>
            <div style={s.testimonialCard}>
              <p style={s.testimonialQuoteText}>
                "Ridelink has been a game-changer for our operations. By consolidating
                container tracking for all our shipping lines into a single platform,
                we've cut our visibility overhead by more than half."
              </p>
              <div style={s.testimonialDivider} />
              <div style={s.testimonialAuthorRow}>
                <div style={s.testimonialAvatar}>K</div>
                <div>
                  <p style={s.testimonialName}>Kofi Mensah</p>
                  <p style={s.testimonialRole}>Head of Logistics · Accra Freight Co.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AIR CARGO TRACKING ── */}
      <section style={s.productSectionAlt} id="air-tracking" className="lp-product-section">
        <div style={{ ...s.productInner, direction: 'ltr' }} className="lp-product-inner">
          <div>
            <div style={s.testimonialCard}>
              <p style={s.testimonialQuoteText}>
                "We started using Ridelink Air Cargo Beta and it has become an invaluable
                asset for our business. It's incredibly easy to access both sea and air
                shipments from the same dashboard."
              </p>
              <div style={s.testimonialDivider} />
              <div style={s.testimonialAuthorRow}>
                <div style={s.testimonialAvatar}>A</div>
                <div>
                  <p style={s.testimonialName}>Amara Diallo</p>
                  <p style={s.testimonialRole}>Operations Director · Lagos Air Cargo</p>
                </div>
              </div>
            </div>
          </div>
          <div style={s.productLeft}>
            <p style={s.eyebrow}>Air Freight</p>
            <h2 style={s.productHeading}>Air Cargo<br />Tracking</h2>
            <p style={s.productPara}>
              Ridelink provides comprehensive air cargo tracking. Instantly track air
              waybills (AWB) of multiple airlines and boost your supply chain visibility
              with a single unified platform.
            </p>
            <ul style={s.productCheckList}>
              {AIR_FEATURES.map((f) => (
                <li key={f} style={s.productCheckItem}>
                  <span style={s.productCheckIcon}>✓</span>
                  {f}
                </li>
              ))}
            </ul>
            <div style={s.productLogoRow}>
              {CARRIERS.filter((c) => AIR_LOGOS.includes(c.name)).map((c) => (
                <img key={c.name} src={c.logo} alt={c.name} style={s.productLogoImg} title={c.name} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICE COVERAGE ── */}
      <section style={s.coverage} id="coverage" className="lp-section">
        <div style={s.sectionInner} className="lp-section-inner">
          
          <h2 style={{ ...s.sectionTitle, color: NAVY, marginBottom: 8 }}>Coverage</h2>
          <p style={{ ...s.sectionSub, marginBottom: 40 }}>Tracking shipments across every major global port and trade lane</p>
        </div>
        <div style={s.coverageMapWrap}>
          <img
            src="/15721164ae6208d98c43a3a27e98525c-removebg-preview.png"
            alt="World coverage map"
            style={s.coverageMapImg}
          />
          {/* Blinking pins — positions as % of map image */}
          {[
            { top: '33%', left: '14%' },        // Los Angeles
            { top: '28%', left: '20%' },        // New York
            { top: '60%', left: '30%' },        // Santos / São Paulo
            { top: '24%', left: '46%' },        // Rotterdam
            { top: '26%', left: '47%' },        // Hamburg
            { top: '55%', left: '49%' },        // Cape Town
            { top: '46%', left: '52%' },        // Mombasa
            { top: '37%', left: '54%' },        // Istanbul
            { top: '41%', left: '56%' },        // Dubai
            { top: '44%', left: '59%' },        // Mumbai
            { top: '49%', left: '63%' },        // Colombo
            { top: '39%', left: '66%' },        // Kolkata
            { top: '52%', left: '70%' },        // Singapore
            { top: '38%', left: '72%' },        // Shanghai
            { top: '43%', left: '73%' },        // Hong Kong
            { top: '33%', left: '75%' },        // Busan
            { top: '35%', left: '78%' },        // Tokyo
            { top: '64%', left: '77%' },        // Sydney
          ].map((pin, i) => (
            <span
              key={i}
              className="coverage-pin"
              style={{
                top: pin.top,
                left: pin.left,
                '--pin-color': '#1B998B',
                animationDelay: `${(i * 0.37) % 2.5}s`,
              }}
            />
          ))}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <Footer />
    </div>
  );
}

const s = {
  root: {
    fontFamily: "var(--font-body)",
    background: "#F5F8FA",
    color: "#1A2730",
    overflowX: "hidden",
  },


  /* HERO */
  hero: {
    position: "relative",
    minHeight: 640,
    background: "#ffffff",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    isolation: "isolate",
  },
  heroGlow: { display: "none" },
  /* Persistent map background layers */
  heroBgMap: {
    position: "absolute",
    inset: 0,
    backgroundImage: "url('/9520baffb4c0b087e2d4b593a015dba4-removebg-preview.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    transition: "opacity 800ms ease, transform 800ms ease",
    zIndex: 0,
    pointerEvents: "none",
  },
  heroBgPin: {
    position: "absolute",
    inset: 0,
    backgroundImage: "url('/map.png')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    transition: "opacity 800ms ease, transform 800ms ease",
    zIndex: 0,
    pointerEvents: "none",
  },
  heroBgTint: {
    position: "absolute",
    inset: 0,
    background: "rgba(255,255,255,0.82)",
    zIndex: 0,
    pointerEvents: "none",
  },
  bgPhaseTag: {
    position: "absolute",
    bottom: 30,
    right: "8%",
    fontSize: 12,
    fontWeight: 600,
    color: NAVY,
    background: `${TEAL}18`,
    border: `1px solid ${TEAL}40`,
    borderRadius: 20,
    padding: "5px 14px",
    letterSpacing: "0.5px",
    zIndex: 10,
    transition: "opacity 0.4s",
  },
  heroContent: {
    width: "100%",
    maxWidth: 600,
    margin: "0 auto 0 8%",
    padding: "60px 0 100px",
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  searchBarContainer: {
    width: "100%",
    marginBottom: 40,
  },
  searchBar: {
    display: "flex",
    alignItems: "center",
    background: "#fff",
    border: `1.5px solid #c8e8e6`,
    borderRadius: 16,
    padding: "6px 6px 6px 16px",
    gap: 8,
    boxShadow: "0 4px 24px rgba(27,58,92,0.08)",
  },
  searchIcon: { fontSize: 14, flexShrink: 0 },
  searchInput: {
    border: "none",
    outline: "none",
    fontSize: 14,
    color: "#334",
    background: "transparent",
    flex: 1,
    padding: "8px 4px",
    fontFamily: "inherit",
  },
  searchDivider: { width: 1, height: 32, background: "#c8e8e6", flexShrink: 0 },
  searchSelect: { flex: 1, minWidth: 0, maxWidth: 200 },
  searchBtn: {
    background: GREEN,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    width: 42,
    height: 42,
    fontSize: 16,
    cursor: "pointer",
    flexShrink: 0,
  },
  heroPriorityLogo: {
    fontFamily: "var(--font-heading)",
    fontSize: 20,
    fontWeight: 800,
    color: NAVY,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
  },
  heroH1: {
    fontSize: 54,
    fontWeight: 800,
    lineHeight: 1.1,
    margin: "0 0 32px",
    color: NAVY,
    letterSpacing: "-1px",
    fontFamily: "var(--font-heading)",
  },
  heroSubIcons: {
    display: "flex",
    flexWrap: "wrap",
    gap: 24,
    justifyContent: "center",
  },
  heroIconBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
  },
  heroIconTeal: {
    color: GREEN,
    display: "flex",
  },
  heroIconText: {
    color: "#4A5B6A",
    fontSize: 13,
    fontWeight: 700,
    textAlign: "center",
  },
  /* LOGGED-IN HERO */
  userWelcome: { display: "flex", alignItems: "center", gap: 16, marginBottom: 28 },
  userAvatar: { width: 56, height: 56, borderRadius: "50%", objectFit: "cover", border: `2px solid ${TEAL}` },
  userAvatarInitials: {
    width: 56, height: 56, borderRadius: "50%",
    background: `${TEAL}22`, color: TEAL,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 22, fontWeight: 700,
  },
  userGreeting: { fontSize: 13, color: "#94A3B8", marginBottom: 2 },
  userName: { fontSize: 24, fontWeight: 700, color: NAVY, letterSpacing: "-0.5px" },
  userCredits: {
    display: "inline-flex", alignItems: "baseline", gap: 8,
    background: `${TEAL}12`, border: `1px solid ${TEAL}30`,
    borderRadius: 12, padding: "10px 18px", marginBottom: 28,
  },
  userCreditsNum: { fontSize: 28, fontWeight: 800, color: TEAL },
  userCreditsSub: { fontSize: 13, color: "#556", fontWeight: 500 },
  trackNowBtn: {
    display: "block", width: "100%", maxWidth: 280,
    background: NAVY, color: "#fff", border: "none",
    borderRadius: 14, padding: "16px 28px",
    fontSize: 16, fontWeight: 700, cursor: "pointer",
    fontFamily: "inherit", marginBottom: 14,
    transition: "background 0.15s, transform 0.15s",
  },
  viewProfileLink: {
    background: "none", border: "none", cursor: "pointer",
    color: "#64748B", fontSize: 13, fontFamily: "inherit",
    textDecoration: "underline", padding: 0,
  },

  heroBadges: { display: "flex", gap: 10, flexWrap: "wrap" },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 20,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 500,
  },
  badgeTeal: { background: `${TEAL}18`, color: TEAL },
  badgeNavy: { background: `${NAVY}12`, color: NAVY },
  badgeMint: { background: "#E0F7F5", color: "#0d9488" },

  /* HERO IMAGE */
  containerWrap: {
    position: "absolute",
    right: 0,
    bottom: 0,
    width: "50%",
    height: "95%",
    zIndex: 1,
    pointerEvents: "none",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
  },
  heroImgFade: { display: "none" },
  slideIndicators: {
    position: "absolute",
    bottom: 30,
    left: "8%",
    display: "flex",
    gap: 8,
    zIndex: 10,
  },
  slideDot: {
    height: 8,
    borderRadius: 4,
    transition: "all 0.3s ease",
  },

  /* CARRIERS MARQUEE */
  carriers: {
    background: "#F4FCFB",
    borderTop: "1px solid #d8eeec",
    borderBottom: "1px solid #d8eeec",
    padding: "32px 0",
    overflow: "hidden",
  },
  carriersLabel: {
    textAlign: "center",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "2px",
    textTransform: "uppercase",
    color: "#94A3B8",
    marginBottom: 20,
  },
  carriersTrack: {
    overflow: "hidden",
    position: "relative",
    maskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
    WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)",
  },
  carriersSlide: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    width: "max-content",
  },
  carrierPill: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff",
    border: "1.5px solid #E2F0EE",
    borderRadius: 40,
    padding: "10px 20px 10px 10px",
    boxShadow: "0 2px 10px rgba(27,58,92,0.06)",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  carrierImg: {
    width: 36,
    height: 36,
    objectFit: "contain",
    borderRadius: 6,
    background: "#f8fafe",
    padding: 2,
  },
  carrierName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#1B3A5C",
    letterSpacing: "0.3px",
  },

  /* HOW IT WORKS */
  how: { padding: "96px 0", background: "#F0F8F8" },
  sectionInner: { maxWidth: 1100, margin: "0 auto", padding: "0 32px" },
  eyebrow: {
    color: TEAL,
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: "2px",
    textTransform: "uppercase",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 40,
    fontWeight: 800,
    letterSpacing: "-1.5px",
    marginBottom: 12,
    color: NAVY,
    fontFamily: "var(--font-heading)",
  },
  sectionSub: { color: "#556", fontSize: 16, marginBottom: 56 },
  steps: { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 28 },
  stepCard: {
    background: "#fff",
    borderRadius: 20,
    padding: 36,
    boxShadow: "0 2px 20px rgba(27,58,92,0.07)",
    border: `1px solid ${TEAL}30`,
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  stepNum: {
    fontSize: 48,
    fontWeight: 800,
    color: TEAL,
    opacity: 0.25,
    lineHeight: 1,
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 10,
    letterSpacing: "-0.5px",
    color: NAVY,
    fontFamily: "var(--font-subheading)",
  },
  stepDesc: { fontSize: 14, color: "#556", lineHeight: 1.6 },

  /* NATIONWIDE */
  nationwide: {
    position: "relative",
    background: DNAV,
    padding: "96px 0",
    overflow: "hidden",
  },
  nationwideGlow: {
    position: "absolute",
    left: "-5%",
    top: "20%",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background: `radial-gradient(circle,${TEAL}28 0%,transparent 70%)`,
    pointerEvents: "none",
  },
  nationwideInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 32px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 64,
    alignItems: "center",
  },
  nationwideImg: {
    borderRadius: 20,
    overflow: "hidden",
    height: 380,
    background: TNAV,
    position: "relative",
  },
  containerStack: { position: "absolute", inset: 0, padding: 20 },
  stackRow: {
    position: "absolute",
    left: 20,
    right: 20,
    height: 80,
    background: `linear-gradient(135deg,${TNAV},${DNAV})`,
    borderRadius: 8,
    border: `1px solid ${TEAL}20`,
  },
  stackGlow: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "40%",
    background: `linear-gradient(transparent,${TEAL}22)`,
  },
  nationwideText: { color: "#fff" },
  tealTag: {
    display: "inline-block",
    background: `${TEAL}20`,
    color: TEAL,
    borderRadius: 24,
    padding: "6px 16px",
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 20,
  },
  nationwideH2: {
    fontSize: 42,
    fontWeight: 800,
    letterSpacing: "-1.5px",
    lineHeight: 1.15,
    marginBottom: 20,
    fontFamily: "var(--font-heading)",
  },
  nationwidePara: {
    color: "rgba(255,255,255,0.5)",
    fontSize: 15,
    lineHeight: 1.7,
    marginBottom: 28,
  },
  checkList: {
    listStyle: "none",
    margin: "0 0 36px",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "rgba(255,255,255,0.8)",
    fontSize: 14,
  },
  checkIcon: { color: TEAL, fontSize: 10 },
  nationwideBtns: { display: "flex", gap: 14 },
  tealBtn: {
    background: TEAL,
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "13px 28px",
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  outlineBtn: {
    background: "transparent",
    color: "rgba(255,255,255,0.8)",
    border: "1.5px solid rgba(255,255,255,0.2)",
    borderRadius: 12,
    padding: "12px 28px",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  /* SERVICES */
  services: { padding: "96px 0", background: "#F0F8F8" },
  serviceCards: {
    display: "grid",
    gridTemplateColumns: "repeat(3,1fr)",
    gap: 24,
    marginTop: 48,
  },
  serviceCard: {
    borderRadius: 20,
    padding: 36,
    height: 200,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    position: "relative",
    overflow: "hidden",
    border: `1px solid ${TEAL}20`,
  },
  serviceEmoji: { fontSize: 36 },
  serviceLabel: {
    color: "#fff",
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: "-0.5px",
    fontFamily: "var(--font-subheading)",
  },
  serviceArrow: {
    position: "absolute",
    bottom: 24,
    right: 24,
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: `${TEAL}30`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: TEAL,
    fontSize: 18,
  },

  /* WHY */
  why: { padding: "96px 0", background: "#fff" },
  whyInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 32px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 64,
    alignItems: "center",
  },
  whyLeft: {},
  whyH2: {
    fontSize: 40,
    fontWeight: 800,
    letterSpacing: "-1.5px",
    marginBottom: 16,
    lineHeight: 1.15,
    color: NAVY,
    fontFamily: "var(--font-heading)",
  },
  whySub: { color: "#556", fontSize: 15, lineHeight: 1.7, marginBottom: 36 },
  featureList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  featureItem: { display: "flex", alignItems: "flex-start", gap: 16 },
  featureIcon: {
    width: 44,
    height: 44,
    background: `${TEAL}18`,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    flexShrink: 0,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 4,
    letterSpacing: "-0.3px",
    color: NAVY,
    fontFamily: "var(--font-subheading)",
  },
  featureDesc: { fontSize: 13, color: "#556", lineHeight: 1.5 },
  whyRight: {},
  mapCard: {
    background: "#E8F7F6",
    borderRadius: 20,
    overflow: "hidden",
    border: `1px solid ${TEAL}30`,
  },
  mapSvg: { width: "100%", display: "block" },
  mapLabel: {
    padding: "16px 20px",
    fontWeight: 600,
    fontSize: 15,
    textAlign: "center",
    color: NAVY,
  },

  /* TRACKING CTA */
  trackCta: {
    position: "relative",
    background: DNAV,
    padding: "80px 0",
    overflow: "hidden",
  },
  trackCtaGlow: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: "50%",
    background: `radial-gradient(ellipse at right center,${TEAL}18 0%,transparent 70%)`,
    pointerEvents: "none",
  },
  trackCtaInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 32px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 64,
    alignItems: "center",
  },
  trackCtaLeft: { color: "#fff" },
  trackCtaTag: {
    display: "inline-block",
    background: `${TEAL}20`,
    color: TEAL,
    borderRadius: 24,
    padding: "6px 16px",
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 20,
  },
  trackCtaH2: {
    fontSize: 42,
    fontWeight: 800,
    letterSpacing: "-1.5px",
    lineHeight: 1.2,
    fontFamily: "var(--font-heading)",
  },
  trackCtaCard: {
    background: "#fff",
    borderRadius: 20,
    padding: 32,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  trackCardTitle: { fontWeight: 700, fontSize: 16, color: NAVY },
  trackCardInput: {
    border: `1.5px solid ${TEAL}40`,
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  },
  trackCardSelect: {
    border: `1.5px solid ${TEAL}40`,
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    background: "#fff",
  },

  /* STATS SECTION */
  statsSection: {
    background: "#fff",
    padding: "72px 0",
    borderTop: "1px solid #E8EEF2",
    borderBottom: "1px solid #E8EEF2",
  },
  statsInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 32px",
  },
  statsHeading: {
    textAlign: "center",
    fontSize: 22,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 52,
    fontFamily: "var(--font-subheading)",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 32,
    maxWidth: 720,
    margin: "0 auto",
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
  },
  statEmoji: { fontSize: 28, marginBottom: 4 },
  statNum: {
    fontSize: 52,
    fontWeight: 800,
    color: NAVY,
    lineHeight: 1,
    fontFamily: "var(--font-heading)",
  },
  statLabel: { fontSize: 14, color: "#64748B", fontWeight: 500 },

  /* PRODUCT FEATURE SECTIONS */
  productSection: { padding: "96px 0", background: "#fff" },
  productSectionAlt: { padding: "96px 0", background: "#F8FAFB" },
  productInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 32px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 80,
    alignItems: "center",
  },
  productLeft: {},
  productHeading: {
    fontSize: 36,
    fontWeight: 800,
    color: NAVY,
    lineHeight: 1.2,
    marginBottom: 16,
    fontFamily: "var(--font-heading)",
  },
  productPara: {
    color: "#64748B",
    fontSize: 15,
    lineHeight: 1.7,
    marginBottom: 28,
  },
  productCheckList: {
    listStyle: "none",
    margin: "0 0 32px",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  productCheckItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    fontSize: 15,
    color: "#2F3E4A",
    fontWeight: 500,
  },
  productCheckIcon: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: `${GREEN}18`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: GREEN,
    fontSize: 11,
    fontWeight: 700,
    flexShrink: 0,
  },
  productLogoRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  productLogoImg: {
    width: 36,
    height: 36,
    objectFit: "contain",
    borderRadius: 6,
    background: "#f8fafe",
    border: "1px solid #E2E8F0",
    padding: 4,
  },

  /* TESTIMONIAL CARD */
  testimonialCard: {
    background: "#fff",
    borderRadius: 20,
    padding: 36,
    border: "1px solid #E2E8F0",
    boxShadow: "0 4px 24px rgba(2,44,67,0.07)",
  },
  testimonialQuoteText: {
    fontStyle: "italic",
    color: "#2F3E4A",
    lineHeight: 1.75,
    fontSize: 15,
    marginBottom: 28,
  },
  testimonialDivider: {
    width: 40,
    height: 2,
    background: GREEN,
    borderRadius: 2,
    marginBottom: 20,
  },
  testimonialAuthorRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  testimonialAvatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: `${NAVY}18`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    flexShrink: 0,
    color: NAVY,
    fontWeight: 700,
    fontFamily: "var(--font-heading)",
  },
  testimonialName: {
    fontSize: 15,
    fontWeight: 700,
    color: NAVY,
    fontFamily: "var(--font-subheading)",
  },
  testimonialRole: { fontSize: 13, color: "#94A3B8", marginTop: 2 },

  /* FOOTER */
  coverage: { padding: "96px 0 64px", background: "#fff" },
  coverageEyebrow: {
    textAlign: "center",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "2px",
    textTransform: "uppercase",
    color: RED,
    marginBottom: 6,
  },
  coverageMapWrap: {
    position: "relative",
    maxWidth: 900,
    margin: "0 auto",
    padding: "0 32px",
  },
  coverageMapImg: {
    width: "100%",
    display: "block",
    opacity: 0.45,
  },

};

const css = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body, input, select, textarea { font-family: var(--font-body); }
  button { font-family: var(--font-body); font-weight: 600; }
  .nav-a:hover { color: #1B998B !important; }
  .contact-btn:hover { background: #13726A !important; transform: translateY(-1px); }
  .search-btn:hover  { background: #011825 !important; }
  .track-now-btn:hover { background: #0A3D5C !important; transform: translateY(-1px); }
  .teal-btn:hover    { background: #13726A !important; transform: translateY(-1px); }
  .outline-btn:hover { border-color: rgba(255,255,255,0.5) !important; color: #fff !important; }
  .step-card:hover   { transform: translateY(-4px); box-shadow: 0 8px 32px rgba(2,44,67,0.14) !important; }
  .service-card:hover { transform: translateY(-6px) scale(1.02); box-shadow: 0 20px 48px rgba(1,24,37,0.35) !important; }
  .container-float { animation: float 4s ease-in-out infinite; }
  @keyframes float {
    0%,100% { transform: translateY(-50%) translateY(0); }
    50%      { transform: translateY(-50%) translateY(-16px); }
  }
  .carriers-slide { animation: scroll-marquee 32s linear infinite; }
  .carriers-slide:hover { animation-play-state: paused; }
  @keyframes scroll-marquee {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .track-card { box-shadow: 0 8px 40px rgba(2,44,67,0.25); }
  .search-bar:focus-within { border-color: #1B998B !important; box-shadow: 0 4px 24px rgba(27,153,139,0.18) !important; }
  input:focus, select:focus { border-color: #1B998B !important; box-shadow: 0 0 0 3px rgba(27,153,139,0.12); }

  /* ── PHASE 1 & 3: Foreground image animations ── */
  @keyframes fgSlideEnter {
    0%   { transform: translateX(80px) scale(0.92); opacity: 0; }
    100% { transform: translateX(0)    scale(1);    opacity: 1; }
  }
  @keyframes fgSlideExit {
    0%   { transform: translateX(0)     scale(1);    opacity: 1; }
    100% { transform: translateX(-80px) scale(0.92); opacity: 0; }
  }
  .hero-fg-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    will-change: transform, opacity;
  }
  .hero-fg-img--enter  { animation: fgSlideEnter 0.8s cubic-bezier(0.16,1,0.3,1) forwards; }
  .hero-fg-img--hold   { opacity: 1; transform: translateX(0) scale(1); }
  .hero-fg-img--exit   { animation: fgSlideExit 0.7s cubic-bezier(0.4,0,1,1) forwards; }
  .hero-fg-img--hidden { opacity: 0; }

  /* ── Text content animations ── */
  @keyframes textSlideIn {
    0%   { transform: translateY(24px); opacity: 0; }
    100% { transform: translateY(0);    opacity: 1; }
  }
  @keyframes textSlideOut {
    0%   { transform: translateY(0);    opacity: 1; }
    100% { transform: translateY(-24px); opacity: 0; }
  }
  .hero-text { will-change: transform, opacity; }
  .hero-text--in     { animation: textSlideIn  0.7s cubic-bezier(0.16,1,0.3,1) forwards; }
  .hero-text--out    { animation: textSlideOut 0.6s cubic-bezier(0.4,0,1,1) forwards; }
  .hero-text--hidden { opacity: 0; }

  /* ── BG-phase tag fade ── */
  .bg-phase-tag { animation: tagFade 0.8s ease forwards; }
  @keyframes tagFade {
    0%   { opacity: 0; transform: translateY(6px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  /* ── COVERAGE PINS ── */
  .coverage-pin {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--pin-color);
    transform: translate(-50%, -50%);
    animation: pin-pulse 2s ease-out infinite;
  }
  .coverage-pin::after {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    border: 2px solid var(--pin-color);
    animation: pin-ring 2s ease-out infinite;
    opacity: 0;
  }
  @keyframes pin-pulse {
    0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
    50%       { transform: translate(-50%, -50%) scale(1.25); opacity: 0.8; }
  }
  @keyframes pin-ring {
    0%   { transform: scale(0.5); opacity: 0.8; }
    100% { transform: scale(2.2); opacity: 0; }
  }

  /* ── MOBILE RESPONSIVE OVERRIDES ── */
  @media (max-width: 768px) {
    /* Header */
    .lp-header-inner { padding: 0 16px !important; }
    .lp-nav-links { display: none !important; }
    .lp-header-actions { display: none !important; }
    
    /* Hero */
    .lp-hero { min-height: 100vh !important; }
    .lp-hero-content {
      margin: 0 !important;
      padding: 120px 24px 60px 24px !important;
      align-items: center !important;
      text-align: center !important;
    }
    .lp-hero-content { width: 100% !important; max-width: 100% !important; }
    .lp-hero-content h1 { font-size: 34px !important; text-align: center !important; }
    .lp-hero-content > div:last-child {
      flex-direction: row !important; flex-wrap: wrap !important; gap: 12px !important; justify-content: center !important;
    }
    .lp-hero-icon-box {
      flex: 0 0 calc(50% - 8px) !important; width: calc(50% - 8px) !important; align-items: center !important;
    }
    
    /* Search Bar */
    .search-bar { flex-direction: column !important; padding: 16px !important; border-radius: 20px !important; }
    .search-bar input { width: 100% !important; padding: 12px 0 !important; }
    .search-bar > div:nth-child(3) { display: none !important; } /* searchDivider */
    .search-bar > div { width: 100% !important; max-width: 100% !important; min-width: 0 !important; flex: unset !important; }
    .search-bar .lp-select__control { width: 100% !important; min-width: 100% !important; border: 1px solid #E2E8F0 !important; border-radius: 8px !important; }
    .search-btn { width: 100% !important; border-radius: 12px !important; margin-top: 8px !important; height: auto !important; padding: 12px !important; }
    
    /* Elements Grid */
    .lp-section { padding: 56px 0 !important; }
    .lp-section-inner, .lp-grid-2, .lp-grid-3 {
      padding: 0 24px !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 32px !important;
    }
    .lp-product-section { padding: 56px 0 !important; }
    .lp-product-inner {
      grid-template-columns: 1fr !important;
      gap: 32px !important;
      padding: 0 24px !important;
    }
    
    .lp-reverse-mobile { flex-direction: column-reverse !important; }
    .lp-hide-mobile { display: none !important; }
    .lp-hero-img-wrap { display: none !important; }
    
    /* Typography Overrides */
    h2, .lp-section-title { font-size: 28px !important; text-align: center !important; }
    .step-card h3 { text-align: center !important; }
    p, .section-sub { text-align: center !important; }

    /* Fix steps text alignment */
    .step-card { display: flex; flex-direction: column; align-items: center; }

  }
`;
